/**
 * PublishedProjectPage - Read-only viewer for published project snapshots (RFC-0295).
 *
 * Route: /published/:shareToken
 * Fetches the published snapshot (public, no auth required) and renders a
 * read-only ReactFlow canvas using the same canvas-v2 node types as the
 * workspace. No Yjs, no WebSocket, no chat sidebar.
 *
 * Admin users see a version picker to browse historical snapshots
 * and the live workspace state.
 */

import { useState, useMemo, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import ReactFlow, { ReactFlowProvider, Background, BackgroundVariant, MiniMap, PanOnScrollMode, useNodesState, useEdgesState, type Node, type Edge } from 'reactflow'
import 'reactflow/dist/style.css'
import {
  nodeTypes,
  edgeTypes,
  CanvasContext,
  type CanvasContextValue,
  NodeHoverContext,
  type NodeHoverContextValue,
} from '../components/canvas-v2'
import { GroupDropTargetProvider } from '../components/canvas-v2/nodes/GroupNode/GroupDropTargetContext'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Copy, Loader2 } from 'lucide-react'
import { usePublishedSnapshot, usePublicationVersions, usePublicationVersion, usePublicationLive, useClonePublishedProject } from '../api/hooks'
import { setPublicationToken, type PublishedChatMessage } from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import { ReadOnlyContext } from '../contexts/ReadOnlyContext'
import { ProjectIdProvider } from '../contexts/ProjectIdContext'
import { CANVAS_HIDDEN_FE_TYPES, YDOC_DESERIALIZERS } from '../generated/nodeDataTypes'
import { toBackendType } from '../hooks/useYCanvas'

// ---------------------------------------------------------------------------
// ReadOnlyChatPanel — displays snapshotted conversation messages
// ---------------------------------------------------------------------------

/** Strip internal code blocks (dag-proposal, story-structure, etc.) */
function stripInternalBlocks(content: string): string {
  return content
    .replace(/```dag-proposal[\s\S]*?```/g, '')
    .replace(/```story-structure[\s\S]*?```/g, '')
    .replace(/```story-direction[\s\S]*?```/g, '')
    .trim()
}

/** Clean AI-generated text by replacing em-dashes */
function cleanAiText(text: string): string {
  return text
    .replace(/\s*—\s*/g, ', ')
    .replace(/\s*–\s*/g, ', ')
}

/** Markdown component overrides matching AgentChat styling */
const markdownComponents = {
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="list-disc pl-4 my-1.5 space-y-0.5">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="list-decimal pl-4 my-1.5 space-y-0.5">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="text-text-secondary">{children}</li>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="my-1.5">{children}</p>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-medium text-text-primary">{children}</strong>
  ),
  em: ({ children }: { children?: React.ReactNode }) => (
    <em className="text-text-secondary">{children}</em>
  ),
  code: ({ children, className }: { children?: React.ReactNode; className?: string }) => {
    const isInline = !className?.includes('language-')
    if (isInline) {
      return (
        <code className="bg-surface-1 px-1 py-0.5 rounded text-xs font-mono text-text-primary">{children}</code>
      )
    }
    return <>{children}</>
  },
  pre: ({ children }: { children?: React.ReactNode }) => (
    <pre className="bg-surface-1 border border-border rounded-lg p-3 my-2 overflow-x-auto text-xs">{children}</pre>
  ),
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="text-base font-medium text-text-primary mt-2 mb-1">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="text-sm font-medium text-text-primary mt-2 mb-1">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="text-sm font-medium text-text-primary mt-1.5 mb-0.5">{children}</h3>
  ),
  a: ({ children }: { href?: string; children?: React.ReactNode }) => (
    <span className="text-text-secondary underline underline-offset-2">{children}</span>
  ),
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote className="border-l border-border pl-2 my-1.5 text-text-muted">{children}</blockquote>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="my-2 overflow-x-auto">
      <table className="min-w-full text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => (
    <thead className="bg-panel">{children}</thead>
  ),
  tbody: ({ children }: { children?: React.ReactNode }) => (
    <tbody className="divide-y divide-border-subtle">{children}</tbody>
  ),
  tr: ({ children }: { children?: React.ReactNode }) => (
    <tr className="border-b border-border-subtle">{children}</tr>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="px-3 py-2 text-left font-semibold text-text-secondary border border-border">{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="px-3 py-2 font-normal text-text-secondary border border-border-subtle">{children}</td>
  ),
}

function ReadOnlyChatPanel({ messages }: { messages: PublishedChatMessage[] }) {
  const [collapsed, setCollapsed] = useState(false)

  // Filter messages the same way AgentChat does:
  // 1. Truncate at the first message with unanswered questions (hide everything after it)
  // 2. Hide assistant messages whose only questions are answered node/cost confirmations (no visible content)
  // 3. Hide user "continue"/"confirm" replies that follow such messages
  // 4. Hide empty assistant messages with no visible content (unless it's the one with unanswered questions)
  const { displayMessages, currentQuestions } = useMemo(() => {
    // Find the first message with unanswered questions
    let firstUnansweredIdx = -1
    for (let i = 0; i < messages.length; i++) {
      if (messages[i].questions?.some((q: Record<string, unknown>) => q.answer == null)) {
        firstUnansweredIdx = i
        break
      }
    }

    // Truncate: only show messages up to and including the first unanswered
    const slice = firstUnansweredIdx === -1
      ? messages
      : messages.slice(0, firstUnansweredIdx + 1)

    const isAutoAcceptedAssistant = (msg: PublishedChatMessage) => {
      if (msg.role !== 'assistant' || !msg.questions?.length) return false
      return msg.questions.every((q: Record<string, unknown>) => {
        if (q.answer == null) return false
        if (q.tool_use_id === '__cost_confirmation__') return true
        return false
      })
    }

    const filtered = slice.filter((msg, i, arr) => {
      // Hide empty assistant messages unless they carry unanswered questions
      const hasUnansweredQuestions = msg.questions?.some((q: Record<string, unknown>) => q.answer == null)
      if (msg.role === 'assistant' && !stripInternalBlocks(msg.content || '').trim() && !hasUnansweredQuestions) return false
      if (isAutoAcceptedAssistant(msg) && !msg.content?.trim()) return false
      if (msg.role === 'user' && (msg.content === 'continue' || msg.content === 'confirm')) {
        const prev = arr[i - 1]
        if (prev && isAutoAcceptedAssistant(prev) && !prev.content?.trim()) return false
      }
      return true
    })

    // Extract unanswered questions from the first unanswered message only
    const currentQuestions = firstUnansweredIdx === -1
      ? []
      : (messages[firstUnansweredIdx].questions || []).filter(
          (q: Record<string, unknown>) => q.answer == null
        )

    return { displayMessages: filtered, currentQuestions }
  }, [messages])

  if (displayMessages.length === 0 && currentQuestions.length === 0) return null

  return (
    <div
      className={`shrink-0 border-r border-border bg-card flex flex-col transition-all duration-200 ${
        collapsed ? 'w-10' : 'w-[360px]'
      }`}
      data-testid="published-chat-panel"
    >
      {/* Collapse toggle */}
      <div className="h-12 border-b border-border flex items-center px-3 shrink-0">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 rounded hover:bg-white/10 text-white/60 hover:text-white transition-colors"
          title={collapsed ? 'Expand chat' : 'Collapse chat'}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            {collapsed ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7M19 19l-7-7 7-7" />
            )}
          </svg>
        </button>
        {!collapsed && (
          <span className="ml-2 text-sm font-medium text-white/80">Conversation</span>
        )}
      </div>

      {/* Messages */}
      {!collapsed && (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {displayMessages.map((msg, i) => {
            const isUser = msg.role === 'user'
            const cleanContent = cleanAiText(stripInternalBlocks(msg.content))

            if (isUser) {
              return (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[85%] px-3 py-2 text-[15px] leading-relaxed text-text-primary bg-white/10 border border-white/10 rounded-lg rounded-br-sm pointer-events-none select-text">
                    <div className="break-words whitespace-pre-wrap">{cleanContent}</div>
                  </div>
                </div>
              )
            }

            return (
              <div key={i} className="space-y-1.5 pointer-events-none select-text">
                <div className="flex items-center gap-1.5">
                  <svg className="w-3 h-3 text-primary" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                  </svg>
                  <span className="text-sm font-medium text-text-muted">Director</span>
                </div>
                {cleanContent && (
                  <div className="text-[15px] leading-[1.6] text-text-primary break-words">
                    <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                      {cleanContent}
                    </Markdown>
                  </div>
                )}
              </div>
            )
          })}

          {/* Current questions — rendered once at the bottom, matching AgentChat */}
          {currentQuestions.map((q: Record<string, unknown>, qi: number) => {
            const options = (q.options || []) as Array<Record<string, unknown>>

            // Single option: inline text style (matches QuestionCard)
            if (options.length === 1) {
              const opt = options[0]
              return (
                <div key={qi} className="my-3 text-base text-text-secondary pointer-events-none select-text">
                  {q.question as string}{' '}
                  <span className="text-text-primary underline underline-offset-2">{(opt.label as string) || (opt.value as string)}</span>
                </div>
              )
            }

            // Multiple options: card style
            return (
              <div key={qi} className="my-4 pointer-events-none select-text">
                {typeof q.question === 'string' && q.question && (
                  <div className="mb-3">
                    <span className="text-base text-text-secondary">{q.question}</span>
                  </div>
                )}
                <div className="flex flex-col gap-1.5">
                  {options.map((opt, oi) => (
                    <div
                      key={oi}
                      className="w-full px-3 py-2 text-left rounded border border-border-subtle opacity-60"
                    >
                      <span className="text-sm font-medium text-text-secondary">{(opt.label as string) || (opt.value as string)}</span>
                      {typeof opt.description === 'string' && opt.description && (
                        <span className="block text-xs text-text-muted mt-0.5">{opt.description}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Deserialize snapshot node data through YDoc deserializers
// ---------------------------------------------------------------------------

function deserializeSnapshotData(
  feType: string,
  raw: Record<string, unknown>,
  projectId: string | undefined,
): Record<string, unknown> {
  const backendType = toBackendType(feType)
  const desers = YDOC_DESERIALIZERS[backendType]
  if (!desers) return { ...raw, readOnly: true, projectId }
  const out: Record<string, unknown> = { readOnly: true, projectId }
  for (const [key, value] of Object.entries(raw)) {
    const deser = desers[key]
    out[key] = deser ? deser(value) : value
  }
  return out
}

// ---------------------------------------------------------------------------
// No-op CanvasContext value for read-only mode
// ---------------------------------------------------------------------------

const READ_ONLY_CANVAS_CONTEXT: CanvasContextValue = {
  addNode: () => '',
  addEdge: () => {},
  removeNode: () => {},
  removeEdge: () => {},
  duplicateNode: () => undefined,
  updateNodeData: () => {},
  persistNodeData: () => {},
  updateNodeParent: () => {},
  executeNode: () => {},
  getNode: () => undefined,
  getNodes: () => [],
  updateNodePosition: () => {},
}

const READ_ONLY_HOVER_CONTEXT: NodeHoverContextValue = {
  hoveredNodeId: null,
  setHoveredNodeId: () => {},
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function PublishedProjectPage() {
  const { projectToken, versionSlug } = useParams<{ projectToken: string; versionSlug: string }>()

  const { user } = useAuth()
  const isAdmin = user?.user_metadata?.role === 'admin'

  // Set publication token for unauthenticated asset URL fallback
  useEffect(() => {
    if (projectToken) {
      setPublicationToken(projectToken)
    }
    return () => setPublicationToken(null)
  }, [projectToken])

  // Version picker state (admin only)
  const [selectedVersion, setSelectedVersion] = useState<'latest' | 'live' | number>('latest')

  // Clone mutation
  const cloneMutation = useClonePublishedProject()

  const handleClone = useCallback(async () => {
    if (!projectToken || !versionSlug) return
    try {
      const result = await cloneMutation.mutateAsync({ projectToken, versionSlug })
      window.open(`/v2/project/${result.id}/workspace`, '_blank')
    } catch {
      // mutation error state is handled by cloneMutation.isError
    }
  }, [projectToken, versionSlug, cloneMutation])

  // Fetch the public snapshot
  const {
    data: publishedData,
    isLoading: snapshotLoading,
    error: snapshotError,
  } = usePublishedSnapshot(projectToken, versionSlug)

  // Admin: fetch version list (only when admin and we have projectId from snapshot)
  const adminProjectId = isAdmin && publishedData ? publishedData.project_id : undefined
  const { data: versions } = usePublicationVersions(adminProjectId)

  // Admin: fetch specific version (when selecting a historical version)
  const { data: versionData } = usePublicationVersion(
    adminProjectId,
    typeof selectedVersion === 'number' ? selectedVersion : undefined
  )

  // Admin: fetch live snapshot
  const { data: liveData } = usePublicationLive(
    selectedVersion === 'live' ? adminProjectId : undefined
  )

  // Determine which snapshot to display
  const displayData = useMemo(() => {
    if (selectedVersion === 'live' && liveData) {
      return {
        projectName: liveData.project_name,
        snapshot: liveData.snapshot,
        messages: [] as PublishedChatMessage[],
      }
    }
    if (typeof selectedVersion === 'number' && versionData) {
      return {
        projectName: versionData.project_name,
        snapshot: versionData.snapshot,
        messages: [] as PublishedChatMessage[],
      }
    }
    if (publishedData) {
      return {
        projectName: publishedData.project_name,
        snapshot: publishedData.snapshot,
        messages: publishedData.messages || [],
      }
    }
    return null
  }, [selectedVersion, publishedData, versionData, liveData])

  // Published mode is always read-only for all viewers
  const isReadOnly = true

  // Convert snapshot to ReactFlow nodes and edges
  const projectId = publishedData?.project_id

  const { initialNodes, initialEdges, viewport } = useMemo(() => {
    if (!displayData?.snapshot) return { initialNodes: [] as Node[], initialEdges: [] as Edge[], viewport: undefined }

    const snap = displayData.snapshot
    // Build nodes, filtering out hidden types (same as useYCanvas), then sort
    // so parents appear before children (ReactFlow requirement)
    const rfNodes: Node[] = (snap.nodes || []).filter((n: Record<string, unknown>) =>
      !CANVAS_HIDDEN_FE_TYPES.has(n.type as string)
    ).map((n: Record<string, unknown>) => {
      const node: Node = {
        id: n.id as string,
        type: n.type as string,
        position: (n.position as { x: number; y: number }) || { x: 0, y: 0 },
        data: deserializeSnapshotData(n.type as string, (n.data as Record<string, unknown>) || {}, projectId),
      }
      // Preserve group parent-child relationships
      if (n.parentNode) node.parentNode = n.parentNode as string
      if (n.extent) node.extent = n.extent as 'parent'
      // Preserve explicit dimensions (group nodes)
      if (n.width != null) node.width = n.width as number
      if (n.height != null) node.height = n.height as number
      return node
    })
    // ReactFlow requires parent nodes before their children in the array
    rfNodes.sort((a, b) => {
      if (a.parentNode && !b.parentNode) return 1
      if (!a.parentNode && b.parentNode) return -1
      return 0
    })

    const rfEdges: Edge[] = (snap.edges || []).map((e: Record<string, unknown>) => ({
      id: e.id as string,
      source: e.source as string,
      target: e.target as string,
      type: (e.type as string) || 'default',
      sourceHandle: e.sourceHandle as string | undefined,
      targetHandle: e.targetHandle as string | undefined,
    }))

    return {
      initialNodes: rfNodes,
      initialEdges: rfEdges,
      viewport: snap.viewport as { x: number; y: number; zoom: number } | undefined,
    }
  }, [displayData, projectId])

  // Mutable state so ReactFlow can update positions on drag
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, , onEdgesChange] = useEdgesState(initialEdges)

  // Re-seed when the snapshot changes (version picker, etc.)
  useEffect(() => { setNodes(initialNodes) }, [initialNodes, setNodes])

  // Loading state
  if (snapshotLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="flex flex-col items-center gap-4">
          <svg className="animate-spin h-8 w-8 text-primary" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="text-white/60">Loading published project...</span>
        </div>
      </div>
    )
  }

  // Error state
  if (snapshotError || !publishedData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-white mb-2">Project Not Found</h1>
          <p className="text-white/60">
            {snapshotError instanceof Error
              ? 'This project link is invalid or has been unpublished.'
              : 'Could not load the published project.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <ProjectIdProvider value={projectId}>
    <ReadOnlyContext.Provider value={isReadOnly}>
      <CanvasContext.Provider value={READ_ONLY_CANVAS_CONTEXT}>
        <NodeHoverContext.Provider value={READ_ONLY_HOVER_CONTEXT}>
          <div className="h-screen w-screen flex flex-col bg-surface" data-testid="published-project-page">
            {/* Header */}
            <header className="h-12 border-b border-border flex items-center px-4 bg-card shrink-0">
              <div className="flex items-center gap-3 flex-1">
                <span className="text-sm font-semibold text-primary">DreamWright</span>
                <span className="text-white/30">|</span>
                <h1 className="text-sm text-white truncate" data-testid="published-project-name">
                  {displayData?.projectName || 'Published Project'}
                </h1>
              </div>

              {/* Admin version picker */}
              {isAdmin && versions && versions.length > 0 && (
                <select
                  className="text-xs bg-surface border border-border rounded px-2 py-1 text-white"
                  value={selectedVersion.toString()}
                  onChange={(e) => {
                    const val = e.target.value
                    if (val === 'latest') setSelectedVersion('latest')
                    else if (val === 'live') setSelectedVersion('live')
                    else setSelectedVersion(parseInt(val, 10))
                  }}
                  data-testid="version-picker"
                >
                  <option value="latest">Latest</option>
                  <option value="live">Live</option>
                  {versions.map((v) => (
                    <option key={v.version_number} value={v.version_number.toString()}>
                      v{v.version_number} - {new Date(v.created_at).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              )}

              {/* Clone button — visible only for logged-in users */}
              {user && (
                <button
                  onClick={handleClone}
                  disabled={cloneMutation.isPending}
                  className="ml-3 flex items-center gap-1.5 text-xs bg-primary/90 hover:bg-primary text-white px-3 py-1.5 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="clone-project-button"
                >
                  {cloneMutation.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                  {cloneMutation.isPending ? 'Cloning...' : 'Clone Project'}
                </button>
              )}

              {cloneMutation.isError && (
                <span className="ml-2 text-xs text-red-400">Clone failed</span>
              )}

              <span className="ml-3 text-xs text-white/40 bg-white/5 px-2 py-1 rounded" data-testid="read-only-badge">
                Read-only
              </span>
            </header>

            {/* Content: Chat Panel + Canvas */}
            <div className="flex-1 flex overflow-hidden">
              {/* Chat Panel (left) */}
              <ReadOnlyChatPanel messages={displayData?.messages || []} />

              {/* Canvas (right) */}
              <div className="flex-1">
                  <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    nodesDraggable={true}
                    nodesConnectable={false}
                    elementsSelectable={true}
                    panOnDrag={true}
                    panOnScroll
                    panOnScrollMode={PanOnScrollMode.Free}
                    zoomOnScroll={false}
                    zoomOnPinch
                    minZoom={0.1}
                    maxZoom={4}
                    defaultViewport={viewport || { x: 0, y: 0, zoom: 0.5 }}
                    fitView={!viewport}
                    className="bg-background canvas-gradient-bg"
                    proOptions={{ hideAttribution: true }}
                  >
                    <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="rgba(255,255,255,0.08)" />
                    <MiniMap
                      className="!bg-card !border !border-border !rounded-lg !shadow-sm overflow-hidden"
                      zoomable
                      pannable
                      nodeColor={(node) => {
                        const status = (node.data as { status?: string })?.status
                        if (status === 'running') return '#22c55e'
                        if (status === 'error') return '#ef4444'
                        if (status === 'success') return '#3b82f6'
                        return '#6b7280'
                      }}
                      maskColor="rgba(0, 0, 0, 0.5)"
                      style={{ width: 160, height: 100 }}
                    />
                  </ReactFlow>
              </div>
            </div>
          </div>
        </NodeHoverContext.Provider>
      </CanvasContext.Provider>
    </ReadOnlyContext.Provider>
    </ProjectIdProvider>
  )
}

// ---------------------------------------------------------------------------
// Wrapped export with ReactFlowProvider + GroupDropTargetProvider
// ---------------------------------------------------------------------------

export default function PublishedProjectPageWrapper() {
  return (
    <ReactFlowProvider>
      <GroupDropTargetProvider>
        <PublishedProjectPage />
      </GroupDropTargetProvider>
    </ReactFlowProvider>
  )
}
